/**
 * SpendPlanTimeline — Horizontal stepper showing spend plan lifecycle:
 *   Created → Submitted → Approved → Completed
 *
 * Each step shows a colored icon + connector. Tooltips surface timestamps and actor info.
 * Derives step state from current status and audit activities.
 */
import {
	alpha,
	Box,
	Stack,
	Step,
	StepConnector,
	stepConnectorClasses,
	StepLabel,
	Stepper,
	styled,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	Check as IconCheck,
	CheckCircle as IconComplete,
	Edit as IconDraft,
	Send as IconSubmit,
	ThumbUp as IconApprove,
} from '@mui/icons-material';
import type { Activity, User, SpendPlanStatus } from '../../../@types';
import { COLORS } from '../../../data';
import { UserStamp } from '../UserStamp/UserStamp';

// Types

export interface SpendPlanTimelineProps {
	status: SpendPlanStatus;
	activities: Activity[];
}

type StepState = 'completed' | 'active' | 'upcoming';

interface TimelineStep {
	label: string;
	description: string;
	timestamp?: string;
	user?: Partial<User> | null;
	state: StepState;
	icon: React.ElementType;
}

// Styled connector

const TimelineConnector = styled(StepConnector)(({ theme }) => ({
	[`&.${stepConnectorClasses.alternativeLabel}`]: {
		top: 20,
	},
	[`& .${stepConnectorClasses.line}`]: {
		height: 2,
		border: 0,
		borderRadius: 1,
		backgroundColor: theme.palette.grey[200],
	},
	[`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
		backgroundColor: alpha(COLORS.info.main, 0.5),
	},
	[`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
		backgroundColor: alpha(COLORS.success.main, 0.5),
	},
}));

// Styled step icon

const StepIconContainer = styled('div')<{
	ownerState: { state: StepState };
}>(({ ownerState }) => {
	const bg =
		ownerState.state === 'completed'
			? COLORS.success.main
			: ownerState.state === 'active'
				? COLORS.info.main
				: '#e0e0e0';

	const shadow =
		ownerState.state === 'active' ? `0 0 0 3px ${alpha(COLORS.info.main, 0.15)}` : 'none';

	return {
		alignItems: 'center',
		backgroundColor: bg,
		borderRadius: '50%',
		boxShadow: shadow,
		color: '#fff',
		display: 'flex',
		height: 42,
		justifyContent: 'center',
		width: 42,
		zIndex: 1,
		transition: 'all 0.2s ease',
	};
});

// Helpers

const formatTimestamp = (iso?: string) => {
	if (!iso) return null;
	const d = new Date(iso);
	return d.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};

const formatTimestampFull = (iso?: string) => {
	if (!iso) return null;
	const d = new Date(iso);
	return d.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const resolveActor = (activity?: Activity): Partial<User> | null => {
	if (!activity) return null;
	return typeof activity.actor === 'object' && activity.actor ? activity.actor : null;
};

/** Map status to a numeric step index (0-based) */
const statusToStep: Record<SpendPlanStatus, number> = {
	DRAFT: 0,
	SUBMITTED: 1,
	APPROVED: 2,
	COMPLETED: 3,
};

// Component

export const SpendPlanTimeline = ({ status, activities }: SpendPlanTimelineProps) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Activities are DESC (newest first). Find most recent of each type.
	const lastCreated = [...activities].reverse().find((a) => a.type === 'SPEND_PLAN_CREATED');
	const lastSubmitted = activities.find((a) => a.type === 'SPEND_PLAN_SUBMITTED');
	const lastApproved = activities.find((a) => a.type === 'SPEND_PLAN_APPROVED');
	const lastCompleted = activities.find((a) => a.type === 'SPEND_PLAN_COMPLETED');

	const activeIdx = statusToStep[status] ?? 0;

	const stepState = (idx: number): StepState =>
		idx < activeIdx ? 'completed' : idx === activeIdx ? 'active' : 'upcoming';

	const steps: TimelineStep[] = [
		{
			label: 'Created',
			description: 'Spend plan created in draft',
			timestamp: lastCreated?.created,
			user: resolveActor(lastCreated),
			state: 'completed', // always complete — plan exists
			icon: IconDraft,
		},
		{
			label: 'Submitted',
			description: 'Submitted for approval',
			timestamp: lastSubmitted?.created,
			user: resolveActor(lastSubmitted),
			state: activeIdx >= 1 ? stepState(1) : 'upcoming',
			icon: IconSubmit,
		},
		{
			label: 'Approved',
			description: 'Reviewed and approved',
			timestamp: lastApproved?.created,
			user: resolveActor(lastApproved),
			state: activeIdx >= 2 ? stepState(2) : 'upcoming',
			icon: IconApprove,
		},
		{
			label: 'Completed',
			description: 'Spend plan finalized',
			timestamp: lastCompleted?.created,
			user: resolveActor(lastCompleted),
			state: activeIdx >= 3 ? 'completed' : 'upcoming',
			icon: IconComplete,
		},
	];

	const completedCount = steps.filter((s) => s.state === 'completed').length;

	const renderStepIcon = (step: TimelineStep) => {
		const StepIcon = step.state === 'completed' ? IconCheck : step.icon;
		return (
			<StepIconContainer ownerState={{ state: step.state }}>
				<StepIcon sx={{ fontSize: 22 }} />
			</StepIconContainer>
		);
	};

	const renderTooltip = (step: TimelineStep) => (
		<Box sx={{ p: 1.25, maxWidth: 280 }}>
			<Typography
				sx={{ fontWeight: 600, fontSize: '1.375rem', color: 'common.white', lineHeight: 1.3 }}>
				{step.label}
			</Typography>
			<Typography
				sx={{
					fontSize: '1.25rem',
					color: 'grey.300',
					display: 'block',
					mt: 0.25,
					lineHeight: 1.4,
				}}>
				{step.description}
			</Typography>
			{step.timestamp && (
				<Typography
					sx={{
						fontSize: '1.25rem',
						display: 'block',
						mt: 0.5,
						fontWeight: 600,
						color: 'grey.100',
					}}>
					{formatTimestampFull(step.timestamp)}
				</Typography>
			)}
			{step.user && (
				<Typography sx={{ fontSize: '1.25rem', color: 'grey.300', display: 'block' }}>
					By: {step.user.firstName} {step.user.lastName}
				</Typography>
			)}
			{step.state === 'upcoming' && (
				<Typography
					sx={{
						fontSize: '1.25rem',
						color: 'grey.400',
						display: 'block',
						mt: 0.25,
						fontStyle: 'italic',
					}}>
					Pending
				</Typography>
			)}
		</Box>
	);

	return (
		<Box
			sx={{
				bgcolor: 'white',
				border: '1px solid',
				borderColor: 'grey.200',
				borderRadius: 2,
				px: { xs: 1, sm: 2 },
				py: 2,
			}}>
			<Stepper
				activeStep={completedCount}
				alternativeLabel={!isMobile}
				connector={<TimelineConnector />}
				orientation={isMobile ? 'vertical' : 'horizontal'}>
				{steps.map((step) => (
					<Step
						key={step.label}
						completed={step.state === 'completed'}
						active={step.state === 'active'}>
						<Tooltip
							title={renderTooltip(step)}
							arrow
							placement={isMobile ? 'right' : 'top'}
							componentsProps={{
								tooltip: { sx: { maxWidth: 'none' } },
							}}>
							<StepLabel
								StepIconComponent={() => renderStepIcon(step)}
								sx={{
									cursor: 'default',
									'& .MuiStepLabel-label': {
										fontWeight: step.state === 'active' ? 600 : 500,
										fontSize: '1.25rem',
										color:
											step.state === 'completed'
												? theme.palette.grey[600]
												: step.state === 'active'
													? theme.palette.grey[700]
													: theme.palette.text.disabled,
										mt: 0.75,
									},
								}}>
								<Stack alignItems="center" spacing={0}>
									<span>{step.label}</span>
									{step.timestamp && step.user && (
										<UserStamp
											user={step.user}
											timestamp={step.timestamp}
											color={step.state === 'completed' ? COLORS.success.main : undefined}
											size={22}
											fontSize="1.125rem"
											sx={{ mt: 0.25 }}
										/>
									)}
									{step.timestamp && !step.user && (
										<Typography
											variant="caption"
											sx={{
												color: step.state === 'completed' ? COLORS.success.main : 'text.secondary',
												fontWeight: 500,
												fontSize: '1.25rem',
												lineHeight: 1.3,
												mt: 0.25,
											}}>
											{formatTimestamp(step.timestamp)}
										</Typography>
									)}
									{step.state === 'active' && !step.timestamp && (
										<Typography
											variant="caption"
											sx={{
												color: COLORS.info.main,
												fontWeight: 500,
												fontSize: '1.25rem',
												lineHeight: 1.3,
												mt: 0.25,
											}}>
											In Progress
										</Typography>
									)}
								</Stack>
							</StepLabel>
						</Tooltip>
					</Step>
				))}
			</Stepper>
		</Box>
	);
};
